import { components } from "@/api-schema";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { VehicleTable } from "@/components/data-table/predefined/vehicle-table";
import { vehicleTableDefaultColumns } from "@/components/data-table/predefined/vehicle-table-columns";
import { DataTableHeightMode, ImplementedColumnDef } from "@/components/data-table/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { MapPinPenIcon } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useRelocationOrderInitStore } from "@/modules/fit/internal/relocation-order/init/_hooks/use-relocation-order-init-store";

type Vehicle = components["schemas"]["Vehicle"] & RowWithIdentifier;

const SELECT_VEHICLE_COLUMNS = [
  vehicleTableDefaultColumns.mobile.columnMobileCompleteWithoutTransportStatus,
  vehicleTableDefaultColumns.desktopAndTablet.columnBrandModelDescription,
  vehicleTableDefaultColumns.desktopAndTablet.columnVinColor,
  vehicleTableDefaultColumns.widescreen.columnBrand,
  vehicleTableDefaultColumns.widescreen.columnModelDescription,
  vehicleTableDefaultColumns.widescreen.columnVin,
  vehicleTableDefaultColumns.widescreen.columnColor,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnOwningBranch,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnCurrentBranch,
] as ImplementedColumnDef<Vehicle, unknown>[];

export function InitSelectVehicleView() {
  const { t } = useTranslation(["common", "fit"]);
  const [_, navigate] = useLocation();

  const reset = useRelocationOrderInitStore((state) => state.reset);
  const setVehicle = useRelocationOrderInitStore((state) => state.setVehicle);

  useEffect(() => {
    reset();
  }, [reset]);

  const handleRowClick = useCallback(
    (vehicle: Vehicle) => {
      setVehicle(vehicle);

      navigate("~/fit/relocation-order/init/select-order-type");
    },
    [navigate, setVehicle],
  );

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("fit:SelectVehicle")}
      subTitle={t("fit:SelectVehicleDescription")}
    >
      <VehicleTable
        heightMode={DataTableHeightMode.Content}
        columns={SELECT_VEHICLE_COLUMNS}
        body={{ owningBranch: ["IS_NOT_NULL"], onlyAvailableForRelocation: true, isBlocked: false }}
        showBlockedFilter={false}
        customRowActions={null}
        onRowClick={handleRowClick}
        features={{ expansion: false }}
        subComponent={null}
        noDataIcon={MapPinPenIcon}
        noDataTitle={t("fit:NoVehiclesForRelocationOrder")}
        noDataDescription={t("fit:NoVehiclesForRelocationOrderDescription")}
        noResultsTitle={t("fit:NoVehiclesForRelocationOrderMatchFilters")}
        noResultsDescription={t("fit:NoVehiclesForRelocationOrderMatchFiltersDescription")}
      />
    </CenteredLayout>
  );
}
